import type { CurrentRegion } from "types/wolfAndBadger"
import {
  DynamicYieldContextPageWithLanguage,
  DynamicYieldLanguage
} from "types/dynamicYield"
import type { DynamicYieldExperience } from "hooks/useDynamicYieldExperience"

export const getDynamicYieldLanguageCode = ({
  currentRegion,
  errorHandler
}: {
  currentRegion: CurrentRegion
  errorHandler?: (errorMessage: string) => void
}): DynamicYieldLanguage => {
  // Dynamic Yield returns a different value for the price field based on the supplied language code. We're mapping our regions to a 'language' code to match the DY field.
  switch (currentRegion) {
    case `uk`:
      return `en_GB`
    case `us`:
      return `en_US`
    case `eu`:
      return `en_DE`
    case `au`:
      return `en_AU`
    case `ca`:
      return `en_CA`
    case `global`:
      return `en_SG`
    default:
      if (errorHandler) {
        errorHandler(
          `Unknown region supplied to getDynamicYieldLanguageCode: ${currentRegion}`
        )
      }
      return `en_SG`
  }
}

export const fetchDynamicYieldExperienceCampaigns = async ({
  groups,
  page,
  dyKey,
  cookiesHeader,
  errorMessageHandler,
  errorExceptionHandler,
  userAgent = ``,
  allowNoCookieConsent = false
}: {
  groups: string[]
  page: DynamicYieldContextPageWithLanguage
  dyKey?: string
  cookiesHeader?: string | null
  errorMessageHandler?: (errorMessage: string) => void
  errorExceptionHandler?: (error: unknown) => void
  userAgent?: string | null
  allowNoCookieConsent?: boolean
}) => {
  if (!dyKey) {
    if (errorMessageHandler) {
      errorMessageHandler(
        `Dynamic Yield Experience Campaigns fetch aborted due to missing dyKey`
      )
    }

    return null
  }

  const cookies = cookiesHeader
    ? Object.fromEntries(
        cookiesHeader.split(`; `).map(cookie => cookie.split(`=`))
      )
    : null

  // eslint-disable-next-line no-underscore-dangle
  const dyId = cookies?._dyid
  // _dyid_server cookie always mirrors the _dyid cookie. It's set by our backend server on the first request after consent is given so that it can be used as a first-party cookie by the on-site SDK. If the user has given consent in the current session and we fetch before the backend server sets the cookie, we can use the _dyid cookie instead as the value will be identical.
  // eslint-disable-next-line no-underscore-dangle
  const dyIdServer = cookies?._dyid_server ?? dyId
  // eslint-disable-next-line no-underscore-dangle
  const dyJSession = cookies?._dyjsession

  // Users that haven't provided cookie consent need to have specific campaigns set up to handle them with non-personalised content. Therefore, the default behaviour for this function will be to prevent non-consenting users from receiving any campaigns unless this is explicitly allowed.
  if (!allowNoCookieConsent && (!dyId || !dyIdServer || !dyJSession)) {
    return null
  }

  const TIMEOUT = 3000

  const options = {
    method: `POST`,
    headers: {
      accept: `application/json`,
      "content-type": `application/json`,
      "dy-api-key": dyKey || ``
    },
    body: JSON.stringify({
      user: {
        active_consent_accepted: !!dyId,
        dyid: dyId,
        dyid_server: dyIdServer
      },
      session: {
        dy: dyJSession
      },
      context: {
        page,
        device: {
          userAgent
        }
      },
      options: {
        // This is set to false as Gatsby Link pre-fetches pages that are visible in the viewport, causing SSR functions to run. The user might never actually visit the page, so we don't want to track it as an implicit pageview and instead report the impression client-side when the page actually loads.
        isImplicitPageview: false,
        returnAnalyticsMetadata: false,
        isImplicitImpressionMode: false,
        isImplicitClientData: false
      },
      selector: {
        groups
      }
    }),
    signal: AbortSignal.timeout(TIMEOUT)
  }

  try {
    const response = await fetch(
      `https://dy-api.eu/v2/serve/user/choose`,
      options
    )
    const responseJson = (await response.json()) as {
      choices?: DynamicYieldExperience[]
    }

    return responseJson?.choices ?? null
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      error?.name === `AbortError` &&
      errorMessageHandler
    ) {
      errorMessageHandler(
        `Dynamic Yield Experience Campaigns fetch aborted due to timeout: ${TIMEOUT}ms`
      )
    } else if (errorExceptionHandler) {
      errorExceptionHandler(error)
    }
    return null
  }
}

export const filterExperiencesByGroup = ({
  experiences,
  groups
}: {
  experiences?: DynamicYieldExperience[]
  groups: string[]
}) => {
  if (
    !experiences ||
    experiences.length === 0 ||
    !groups ||
    groups.length === 0
  )
    return null

  const filteredExperiences = experiences.filter(experience =>
    groups.some(group => experience?.groups?.includes(group))
  )

  return filteredExperiences.length > 0 ? filteredExperiences : null
}

export const dynamicYieldDebugLog = (
  ...args: Parameters<typeof console.log>
) => {
  if (typeof localStorage !== `undefined`) {
    const debug = localStorage.getItem(`dynamicYieldDebug`)
    if (debug === `true`) {
      // eslint-disable-next-line no-console
      console.log(`\x1B[1mDY Debug:`, ...args)
    }
  }
}
